// Aceitar e iniciar a viagem (modo viagem, 0.23.0, pedido do titular em
// 12/10/2026): depois do Raio-X, a carga aceita vira a viagem (ou entra na
// viagem em andamento do caminhão), com o previsto do Raio-X: no fim da
// viagem, o app compara o previsto com o realizado.
//   #/viagens/iniciar?origem=…&uf_origem=…&destino=…&uf_destino=…&valor=…&km=…
//     &tipo=…&eixos=…&caminhao=…&volta=vazia|carregada&reserva=…&consumo=…&diesel=…
//     &comp=valor:km;valor:km&tabela=A
import { clientes, colaboradores, despesas, eventosDaViagem, fretes, licencas, manutencao, veiculos, viagens } from '../dados.js';
import { h, trocar } from '../lib/dom.js';
import { mensagemDeErro } from '../lib/erros.js';
import { data, dinheiro, lerInteiro, lerNumero, numeroBR } from '../lib/formato.js';
import { porVeiculo, situacaoDaPlaca } from '../lib/licencas.js';
import { kmAtual } from '../lib/manutencao.js';
import { lugarTexto, partesDasDespesas, planoDoRaioX, ritmoDaEmpresa } from '../lib/modo-viagem.js';
import { custoDosNumeros, raioX } from '../lib/raiox.js';
import { UFS } from '../lib/validacao.js';
import { nestaTela } from '../rotas.js';
import { hojeDaEmpresa, podeGravar } from '../sessao.js';
import { casca } from '../ui/casca.js';
import { avisar, campo, carregando, faixa, formulario, vazio } from '../ui/componentes.js';
import { comAbastecimentos, dadosDoModoViagem, fazerPrevisto } from './modo-viagem.js';
import { fonteDoEscritorio } from './raiox.js';
import { textoCaminhao } from './viagens.js';

const numero = (v) => {
  const n = lerNumero(v);
  return n === null || Number.isNaN(n) ? null : n;
};

// O pedido que veio do Raio-X (ou null, quando falta o essencial).
export function lerPedido(busca) {
  const g = (k) => busca?.get?.(k) ?? null;
  const p = {
    origem: g('origem'), uf_origem: g('uf_origem'), destino: g('destino'), uf_destino: g('uf_destino'),
    valor: numero(g('valor')), km: lerInteiro(g('km')), tipo: g('tipo') || 'carga_geral', eixos: Number(g('eixos')) || 5,
    caminhao: g('caminhao'), voltaVazia: g('volta') !== 'carregada', reserva: numero(g('reserva')) ?? 0,
    consumo: numero(g('consumo')), diesel: numero(g('diesel')), tabela: g('tabela') || 'A',
    complementos: (g('comp') ?? '').split(';').filter(Boolean).map((par) => {
      const [valor, kmExtra] = par.split(':');
      return { valor: Number(valor) || 0, kmExtra: Number(kmExtra) || 0 };
    }).filter((c) => c.valor > 0),
  };
  if (!p.origem || !UFS.includes(p.uf_origem) || !p.destino || !UFS.includes(p.uf_destino) || !(p.valor > 0) || !(p.km > 0)) return null;
  return p;
}

const PEDAGIO = [
  ['empresa', 'A empresa paga', 'na tag ou em dinheiro'],
  ['contratante', 'O contratante paga', 'vale-pedágio ou a tag dele'],
  ['embutido', 'Já vem no frete', 'o valor está dentro do frete'],
];

export function telaIniciarViagem({ busca }) {
  const tela = nestaTela();
  // O formulário não é redesenhado quando chegam dados (perderia o que foi escolhido).
  const corpo = h('div', { class: 'secao', 'data-atualiza-sozinha': '' }, carregando());
  (async () => {
    const pedido = lerPedido(busca);
    if (!pedido) {
      trocar(corpo, vazio({
        simbolo: 'rota', titulo: 'Faça o Raio-X da carga primeiro',
        texto: 'A viagem começa pelo Raio-X: com a carga aceita, toque em "Aceitar e iniciar a viagem".',
        acao: h('a', { class: 'botao', href: '#/raiox' }, 'Abrir o Raio-X do frete'),
      }));
      return;
    }
    const [fonte, lista, frota, pessoas, clis, lics, leituras, itens] = await Promise.all([
      fonteDoEscritorio(), viagens.listar(), veiculos.listar(), colaboradores.listar(), clientes.listar(), licencas.listar(),
      despesas.leituras(), manutencao.listar(),
    ]);
    trocar(corpo, montar({ pedido, fonte, lista, frota, pessoas, clis, lics, leituras, itens, tela }));
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo', 'Não deu para abrir'));
  });
  return casca({ titulo: 'Aceitar e iniciar a viagem', voltar: '/raiox', conteudo: [corpo] });
}

function montar({ pedido, fonte, lista, frota, pessoas, clis, lics, leituras, itens, tela }) {
  const hoje = hojeDaEmpresa();
  const porV = porVeiculo(lics);
  const tratores = frota.filter((x) => x.tipo === 'trator' && x.ativo);
  const motoristas = pessoas.filter((p) => p.funcao === 'motorista' && p.ativo);
  if (tratores.length === 0 || motoristas.length === 0) {
    return vazio({
      simbolo: 'caminhao',
      titulo: tratores.length === 0 ? 'Cadastre um caminhão primeiro' : 'Cadastre um motorista primeiro',
      texto: 'A viagem é sempre de um caminhão com um motorista.',
      acao: h('a', { class: 'botao', href: tratores.length === 0 ? '#/veiculos/novo?tipo=trator' : '#/colaboradores/novo' },
        tratores.length === 0 ? 'Cadastrar caminhão' : 'Cadastrar motorista'),
    });
  }
  const aberta = (veiculoId) => lista.find((v) => v.veiculo_id === veiculoId && v.status === 'aberta') ?? null;
  const ultimoMotorista = (veiculoId) => lista.find((v) => v.veiculo_id === veiculoId)?.motorista_id ?? '';
  const caminhaoInicial = tratores.some((t) => t.id === pedido.caminhao) ? pedido.caminhao : tratores[0].id;
  const kmDoCaminhao = (id) => kmAtual(frota.find((v) => v.id === id), leituras, itens)?.km ?? null;
  const rota = { origem: pedido.origem, uf_origem: pedido.uf_origem, destino: pedido.destino, uf_destino: pedido.uf_destino };
  let numeros = null;

  const previa = h('div', { class: 'cartao previa-do-previsto' }, h('div', { class: 'cartao-corpo' }, carregando('Fazendo o previsto…')));
  const avisoAberta = h('div');
  const camposDaViagemNova = h('div', { class: 'lista' });

  // O previsto com o que está na tela agora.
  const calcular = (d) => {
    if (!numeros) return null;
    const custo = custoDosNumeros(numeros, { veiculoId: d.caminhao, reserva: pedido.reserva, consumo: pedido.consumo, diesel: pedido.diesel });
    if (!custo.porKm) return { semCusto: true };
    const r = raioX({
      valor: pedido.valor, km: pedido.km, voltaVazia: pedido.voltaVazia, custo, tabela: pedido.tabela, tipo: pedido.tipo, eixos: pedido.eixos,
      complementos: pedido.complementos,
    });
    const valorPedagio = numero(d.pedagio_valor);
    const pedagio = d.pedagio_modo === 'contratante' ? { modo: 'contratante' }
      : valorPedagio !== null ? { modo: d.pedagio_modo, valor: valorPedagio } : null;
    const plano = planoDoRaioX({ r, custo, rota, voltaVazia: pedido.voltaVazia, ritmo: ritmoDaEmpresa(numeros?.aprendido), pedagio, feitoEm: new Date().toISOString() });
    return { r, custo, plano };
  };
  const mostrarPrevia = () => {
    const d = lerFormulario();
    const c = calcular(d);
    const corpoDaPrevia = previa.firstChild;
    if (!c) return;
    if (c.semCusto) {
      trocar(corpoDaPrevia, h('span', { class: 'cartao-linha' }, 'Sem o consumo do caminhão, o previsto fica sem o custo: lance os abastecimentos com o km do painel ou digite o consumo no Raio-X.'));
      return;
    }
    const p = c.plano;
    trocar(corpoDaPrevia,
      h('span', { class: 'cartao-titulo' }, `Previsto: sobra ${dinheiro(p.resultado)}`),
      h('span', { class: 'cartao-linha' }, `Frete ${dinheiro(p.receita)} − custos ${dinheiro(p.custos.total)}`),
      h('span', { class: 'cartao-linha' }, `Diesel ${dinheiro(p.custos.diesel)} · pedágio ${dinheiro(p.custos.pedagio)} · despesas ${dinheiro(p.custos.porDia + p.custos.outras)} · reserva ${dinheiro(p.custos.reserva)} · motorista e agenciamento ${dinheiro(p.custos.motorista)}`),
      h('span', { class: 'cartao-linha' }, `${numeroBR(p.km.total)} km${pedido.voltaVazia ? ' (com a volta vazia)' : ''} · uns ${String(p.dias).replace('.', ',')} dias`));
  };

  const form = formulario({
    filhos: [
      h('div', { class: 'cartao' }, h('div', { class: 'cartao-corpo' },
        h('span', { class: 'cartao-titulo' }, `${lugarTexto(pedido.origem, pedido.uf_origem)} → ${lugarTexto(pedido.destino, pedido.uf_destino)}`),
        h('span', { class: 'cartao-linha' }, `Frete de ${dinheiro(pedido.valor)}${pedido.complementos.length > 0 ? ` + ${pedido.complementos.length === 1 ? '1 complemento' : `${pedido.complementos.length} complementos`}` : ''} · ${numeroBR(pedido.km)} km carregado`))),
      campo({
        rotulo: 'Caminhão', nome: 'caminhao', tipo: 'select', obrigatorio: true, valor: caminhaoInicial,
        opcoes: tratores.map((t) => [t.id, textoCaminhao(t)]),
      }),
      avisoAberta,
      camposDaViagemNova,
      campo({ rotulo: 'Cliente (opcional)', nome: 'cliente_id', tipo: 'select', valor: '', opcoes: [['', 'Sem cliente'], ...clis.filter((c) => c.ativo).map((c) => [c.id, c.nome])] }),
      campo({ rotulo: 'Mercadoria (opcional)', nome: 'mercadoria', valor: '' }),
      campo({ rotulo: 'Quem paga o pedágio', nome: 'pedagio_modo', tipo: 'segmentos', valor: 'empresa', opcoes: PEDAGIO }),
      campo({ rotulo: 'Pedágio da viagem (R$)', nome: 'pedagio_valor', formato: 'dinheiro', atributos: { inputmode: 'decimal' }, dica: '' }),
      campo({
        rotulo: 'A viagem começa agora', nome: 'agora', tipo: 'caixa', valor: true,
        dica: 'O app já marca a saída. Desmarque se o motorista vai tocar em "Iniciar viagem" no app dele.',
      }),
      previa,
    ],
    textoBotao: 'Iniciar viagem',
    textoEnviando: 'Iniciando…',
    somenteLeitura: !podeGravar(),
    validar: (d) => {
      const erros = {};
      if (!d.caminhao) erros.caminhao = 'Escolha o caminhão.';
      if (!aberta(d.caminhao)) {
        if (!d.motorista_id) erros.motorista_id = 'Escolha o motorista.';
        if (!d.data_saida) erros.data_saida = 'Informe a data de saída.';
        else if (!situacaoDaPlaca(porV[d.caminhao] ?? [], d.data_saida).ativa) {
          erros.caminhao = `A placa deste caminhão não estava ativa em ${data(d.data_saida)}. Ative a placa em Veículos.`;
        }
        if (d.km_saida && !(lerInteiro(d.km_saida) >= 0)) erros.km_saida = 'Só números.';
      }
      const pedagio = numero(d.pedagio_valor);
      if (d.pedagio_modo === 'embutido' && !(pedagio > 0)) erros.pedagio_valor = 'Diga quanto do frete é o pedágio.';
      if (pedagio !== null && !(pedagio >= 0 && pedagio < pedido.valor)) erros.pedagio_valor = 'Confira o valor do pedágio.';
      return erros;
    },
    aoEnviar: async (d) => {
      const c = calcular(d);
      let viagem = aberta(d.caminhao);
      const nova = !viagem;
      const diaDaCarga = nova ? d.data_saida : hoje;
      if (nova) {
        viagem = await viagens.criar({
          veiculo_id: d.caminhao, motorista_id: d.motorista_id, data_saida: d.data_saida,
          km_saida: d.km_saida ? lerInteiro(d.km_saida) : null, observacao: null,
        });
      }
      const valorPedagio = numero(d.pedagio_valor);
      const frete = await fretes.criar({
        viagem_id: viagem.id,
        cliente_id: d.cliente_id || null,
        data_carregamento: diaDaCarga,
        origem_cidade: pedido.origem, origem_uf: pedido.uf_origem, destino_cidade: pedido.destino, destino_uf: pedido.uf_destino,
        mercadoria: d.mercadoria?.trim() || null,
        valor_frete: pedido.valor,
        km_estimado: pedido.km,
        pedagio_modo: d.pedagio_modo === 'contratante' ? 'contratante_tag' : d.pedagio_modo === 'embutido' ? 'embutido' : 'sem',
        pedagio_valor: d.pedagio_modo === 'embutido' ? valorPedagio : 0,
      });
      // O previsto: o do Raio-X (viagem nova) ou o da viagem com as cargas dela.
      try {
        if (nova && c?.plano) {
          const plano = await comAbastecimentos({ ...c.plano, trechos: c.plano.trechos.map((t) => (t.tipo === 'carregado' ? { ...t, frete_id: frete.id } : t)) },
            { tanque: frota.find((v) => v.id === d.caminhao)?.tanque_litros });
          await eventosDaViagem.criar({ viagem_id: viagem.id, tipo: 'plano', momento: new Date().toISOString(), dados: { plano } }, { esperarServidor: false });
        } else if (!nova) {
          await fazerPrevisto(await dadosDoModoViagem(viagem));
        }
      } catch (erro) {
        console.warn(`Aceitar e iniciar: previsto: ${erro?.message ?? erro}`);
      }
      const jaIniciada = !nova && (await eventosDaViagem.daViagem(viagem.id)).some((e) => e.tipo === 'inicio');
      if (d.agora && !jaIniciada) {
        await eventosDaViagem.criar({
          viagem_id: viagem.id, tipo: 'inicio', momento: new Date().toISOString(), km: nova && d.km_saida ? lerInteiro(d.km_saida) : null,
        }, { esperarServidor: false });
      }
      avisar(nova ? 'Viagem iniciada. Boa estrada!' : 'Carga lançada na viagem em andamento.');
      tela.ir(`/viagens/${viagem.id}`, { substituir: true });
    },
  });

  function lerFormulario() {
    const valor = (nome) => form.querySelector(`[name="${nome}"]`)?.value ?? '';
    return {
      caminhao: valor('caminhao'),
      pedagio_modo: form.querySelector('[name="pedagio_modo"]:checked')?.value ?? 'empresa',
      pedagio_valor: valor('pedagio_valor'),
    };
  }

  // A viagem em andamento do caminhão escolhido: a carga entra nela.
  const mostrarViagemDoCaminhao = () => {
    const id = form.querySelector('[name="caminhao"]').value;
    const v = aberta(id);
    if (v) {
      trocar(avisoAberta, faixa(`Este caminhão está na viagem que saiu em ${data(v.data_saida)} (em andamento): a carga entra nela e o previsto é refeito com as cargas da viagem.`, 'info'));
      trocar(camposDaViagemNova);
      form.querySelector('button[type="submit"]').textContent = 'Lançar a carga na viagem';
      return;
    }
    trocar(avisoAberta);
    const sugerido = ultimoMotorista(id);
    const kmDele = kmDoCaminhao(id);
    trocar(camposDaViagemNova,
      campo({
        rotulo: 'Motorista', nome: 'motorista_id', tipo: 'select', obrigatorio: true,
        valor: motoristas.some((m) => m.id === sugerido) ? sugerido : motoristas.length === 1 ? motoristas[0].id : '',
        opcoes: [['', 'Escolha o motorista'], ...motoristas.map((m) => [m.id, m.apelido ? `${m.nome} (${m.apelido})` : m.nome])],
      }),
      h('div', { class: 'grade-2' },
        campo({ rotulo: 'Saída da base', nome: 'data_saida', tipo: 'date', obrigatorio: true, valor: hoje }),
        campo({ rotulo: 'Km no painel', nome: 'km_saida', formato: 'km', valor: kmDele !== null ? numeroBR(kmDele) : '', dica: 'Opcional.', atributos: { inputmode: 'numeric' } })));
    form.querySelector('button[type="submit"]').textContent = 'Iniciar viagem';
  };
  const mostrarDicaDoPedagio = () => {
    const d = lerFormulario();
    const dica = form.querySelector('[data-campo="pedagio_valor"] .dica');
    const campoValor = form.querySelector('[data-campo="pedagio_valor"]');
    campoValor.hidden = d.pedagio_modo === 'contratante';
    if (!dica || !numeros) return;
    const custo = custoDosNumeros(numeros, { veiculoId: d.caminhao, reserva: pedido.reserva });
    const media = partesDasDespesas(custo).pedagio;
    const kmTotal = pedido.km * (pedido.voltaVazia ? 2 : 1);
    dica.textContent = d.pedagio_modo === 'embutido'
      ? 'Quanto do frete é o pedágio (o app conta como gasto da viagem).'
      : media > 0 ? `Em branco, o app usa a média da frota: uns ${dinheiro(media * kmTotal)} nesta viagem.` : 'Em branco, sem pedágio no previsto (a frota ainda não tem pedágio lançado).';
  };
  form.querySelector('[name="caminhao"]').addEventListener('change', () => {
    mostrarViagemDoCaminhao();
    mostrarPrevia();
  });
  for (const el of form.querySelectorAll('[name="pedagio_modo"], [name="pedagio_valor"]')) {
    el.addEventListener('input', () => {
      mostrarDicaDoPedagio();
      mostrarPrevia();
    });
    el.addEventListener('change', () => {
      mostrarDicaDoPedagio();
      mostrarPrevia();
    });
  }
  mostrarViagemDoCaminhao();
  fonte.numeros().then((n) => {
    numeros = n;
    if (!form.isConnected) return;
    mostrarDicaDoPedagio();
    mostrarPrevia();
  }).catch((erro) => {
    trocar(previa.firstChild, h('span', { class: 'cartao-linha' }, `Não deu para fazer o previsto agora: ${mensagemDeErro(erro)}`));
  });
  return form;
}
